// gioco/stanzaGruppo.ts — la partita a 4 vista dal telefono (v013): entra nella stanza con il token del posto, manda le
// mosse, riceve lo stato e i segnali WebRTC degli altri tre. Tutto ciò che conta lo decide il server.
import { useSyncExternalStore } from "react";
import { tempoReale } from "../servizi/tempoReale.ts";
import { idDispositivo } from "../servizi/dispositivo.ts";
import { dimenticaGruppo } from "../servizi/gruppi.ts";
import type { AzioneProvaGruppo, CodiceErroreWs, DatiSegnale, MessaggioClient, MessaggioServer } from "../../../condiviso/protocollo.ts";
import { ePosto, type Posto, type VistaGruppo } from "../../../condiviso/gruppo.ts";

export interface StatoStanzaGruppo {
  passo: "fuori" | "entrando" | "dentro" | "errore";
  codice: string | null;
  token: string | null;
  posto: Posto | null;
  vista: VistaGruppo | null;
  errore: CodiceErroreWs | null;
}

const FUORI: StatoStanzaGruppo = { passo: "fuori", codice: null, token: null, posto: null, vista: null, errore: null };
const ERRORI_DEFINITIVI: readonly CodiceErroreWs[] = ["sfida_non_trovata", "stanza_piena", "bloccato", "sostituito"];

class StanzaGruppo {
  stato: StatoStanzaGruppo = FUORI;
  private ascoltatori = new Set<() => void>();
  private ascoltatoriSegnale = new Set<(da: Posto, d: DatiSegnale, sessione: string | undefined) => void>();
  private stacca: (() => void)[] = [];

  iscrivi = (f: () => void): (() => void) => {
    this.ascoltatori.add(f);
    return () => this.ascoltatori.delete(f);
  };

  private imposta(n: Partial<StatoStanzaGruppo>): void {
    this.stato = { ...this.stato, ...n };
    this.ascoltatori.forEach((f) => f());
  }

  entra(codice: string, token: string): void {
    this.esciLocale();
    this.imposta({ ...FUORI, passo: "entrando", codice, token });
    this.stacca.push(tempoReale.ascolta((m) => this.ricevi(m)));
    // A ogni (ri)collegamento si rientra con il token: la partita continua da dove era.
    this.stacca.push(
      tempoReale.alCollegamento(() => {
        if (this.stato.codice && this.stato.token && this.stato.passo !== "errore") {
          tempoReale.invia({ tipo: "entra_gruppo", codice: this.stato.codice, token: this.stato.token, dispositivo: idDispositivo() });
        }
      }),
    );
  }

  /** Uscita volontaria: chi è in gara è fuori (gli altri continuano). Il token resta: si può rientrare dal link. */
  esci(): void {
    if (this.stato.passo === "dentro") tempoReale.invia({ tipo: "esci" });
    this.esciLocale();
  }

  private esciLocale(): void {
    this.stacca.forEach((f) => f());
    this.stacca = [];
    this.stato = FUORI;
    this.ascoltatori.forEach((f) => f());
  }

  suSegnale(f: (da: Posto, d: DatiSegnale, sessione: string | undefined) => void): () => void {
    this.ascoltatoriSegnale.add(f);
    return () => this.ascoltatoriSegnale.delete(f);
  }

  private manda(m: MessaggioClient): void {
    if (this.stato.passo === "dentro") tempoReale.invia(m);
  }
  segnale(con: Posto, dati: DatiSegnale, sessione?: string): void {
    this.manda({ tipo: "segnale", dati, con, ...(sessione ? { sessione } : {}) });
  }
  pronto(valore = true): void {
    this.manda({ tipo: "pronto", valore });
  }
  avvia(): void {
    this.manda({ tipo: "avvia" });
  }
  risata(su: Posto, t: number): void {
    this.manda({ tipo: "risata", soggetto: su === this.stato.posto ? "io" : "avversario", su, t: Math.round(t) });
  }
  haRiso(contro: Posto): void {
    this.manda({ tipo: "ha_riso", contro });
  }
  rispondi(conferma: boolean): void {
    this.manda({ tipo: "rispondi", conferma });
  }
  /** v038b: audio spento/acceso. */
  muto(valore: boolean): void {
    this.manda({ tipo: "muto", valore });
  }
  /** v042: sì o no al video delle risate e alle foto della partita (come nell'1 contro 1). */
  consenso(cosa: "clip" | "foto", consenso: boolean): void {
    this.manda({ tipo: cosa, consenso });
  }
  /** v039: i pulsanti del tester nella partita a 4 con i bot. */
  provaGruppo(azione: AzioneProvaGruppo, su?: Posto): void {
    this.manda({ tipo: "prova_gruppo", azione, ...(su ? { su } : {}) });
  }
  volto(presente: boolean): void {
    this.manda({ tipo: "volto", presente });
  }

  private ricevi(m: MessaggioServer): void {
    switch (m.tipo) {
      case "entrato_gruppo":
        if (m.codice !== this.stato.codice) return;
        this.imposta({ passo: "dentro", posto: m.posto, errore: null });
        return;
      case "gruppo":
        if (this.stato.passo !== "dentro") return;
        this.imposta({ vista: m.vista });
        return;
      case "segnale": {
        const da = m.da;
        if (this.stato.passo === "dentro" && ePosto(da)) this.ascoltatoriSegnale.forEach((f) => f(da, m.dati, m.sessione));
        return;
      }
      case "errore":
        if (this.stato.passo !== "fuori" && ERRORI_DEFINITIVI.includes(m.codice)) {
          if (m.codice !== "sostituito" && this.stato.codice) dimenticaGruppo(this.stato.codice);
          this.stacca.forEach((f) => f());
          this.stacca = [];
          this.imposta({ passo: "errore", errore: m.codice });
        }
        return;
      default:
        return;
    }
  }
}

export const stanzaGruppo = new StanzaGruppo();

export function useStanzaGruppo(): StatoStanzaGruppo {
  return useSyncExternalStore(stanzaGruppo.iscrivi, () => stanzaGruppo.stato);
}
